import { Landmark, Utensils, Mountain, ShoppingBag, Moon, Compass, Coffee, Camera, Trophy, Waves, Map, Users } from 'lucide-react';
import { theme } from '../../../styles/theme';
import { useIsMobile } from '../../../hooks/useIsMobile';
import connieUmbrella from '../../../assets/connie-japanese-umbrella-no-bckgn.png';
import type { FormData } from '../../../types';
import { CtaButton } from './shared';
import type { LucideIcon } from 'lucide-react';

const INTERESTS: { id: string; label: string; icon: LucideIcon }[] = [
  { id: 'culture',      label: 'Culture',           icon: Landmark },
  { id: 'food',         label: 'Food',              icon: Utensils },
  { id: 'nature',       label: 'Nature',            icon: Mountain },
  { id: 'shopping',     label: 'Shopping',          icon: ShoppingBag },
  { id: 'nightlife',    label: 'Nightlife',         icon: Moon },
  { id: 'adventure',    label: 'Adventure',         icon: Compass },
  { id: 'relaxation',   label: 'Relaxation',        icon: Coffee },
  { id: 'sightseeing',  label: 'Sightseeing',       icon: Camera },
  { id: 'sports',       label: 'Sports',            icon: Trophy },
  { id: 'water_sports', label: 'Water Sports',      icon: Waves },
  { id: 'excursions',   label: 'Excursions',        icon: Map },
  { id: 'family',       label: 'Family Activities', icon: Users },
];

interface Props { form: FormData; onChange: (f: keyof FormData, v: unknown) => void; onNext: () => void; onBack?: () => void; }

export function StepVibes({ form, onChange, onNext, onBack }: Props) {
  const mobile = useIsMobile();
  const f = mobile ? theme.form.mobile : theme.form;
  const toggle = (id: string) => {
    onChange('interests', form.interests.includes(id)
      ? form.interests.filter(i => i !== id)
      : [...form.interests, id]);
  };

  return (
    <div style={{ maxWidth: mobile ? '100%' : 'min(80%, 700px)', margin: '0 auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.md, marginBottom: mobile ? theme.spacing['2xl'] : theme.spacing['4xl'] }}>
        <img src={connieUmbrella} alt="Connie" referrerPolicy="no-referrer"
          style={{ width: 'auto', height: mobile ? 'min(12vh, 100px)' : 'min(20vh, 250px)', objectFit: 'contain' }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: f.headingSize, fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0, letterSpacing: '-0.02em', textAlign: 'center' }}>
            "What gets you{' '}
            <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>excited?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: f.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xs} 0 0`, textAlign: 'center' }}>
            Our <strong style={{ color: theme.colors.teal }}>NLP-to-API engine</strong> is instantly translating your selections into a custom itinerary.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: theme.spacing.md, marginBottom: theme.spacing.sm }}>
        {INTERESTS.map(({ id, label, icon: Icon }) => {
          const active = form.interests.includes(id);
          return (
            <button key={id} type="button" onClick={() => toggle(id)}
              className={active ? 'chip-pop' : ''}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.sm,
                padding: `${theme.spacing.lg} ${theme.spacing.md}`,
                borderRadius: theme.borderRadius.xl,
                border: `2px solid ${active ? theme.colors.teal : theme.colors.border}`,
                background: active ? theme.colors.teal : 'white',
                cursor: 'pointer', transition: 'all 0.15s', position: 'relative',
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: '50%',
                background: active ? theme.colors.whiteAlpha10 : theme.colors.tealBg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={20} color={active ? 'white' : theme.colors.teal} />
              </div>
              <span style={{ fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.semibold, color: active ? 'white' : theme.colors.navy, textAlign: 'center', lineHeight: 1.3 }}>{label}</span>
            </button>
          );
        })}
      </div>

      <CtaButton
        label={form.interests.length ? `${form.interests.length} picked — continue` : 'Pick at least one'}
        onClick={onNext}
        disabled={form.interests.length === 0}
        onBack={onBack}
      />
    </div>
  );
}
